import React, { useCallback, useState } from 'react';
import { useHistory, useLocation, useParams } from 'react-router-dom';

import { getAuthClient } from '../../utils/auth';
import { buildYourTourApiUrl } from '../../utils/ApiHelpers';
import { useFavoriteButtons } from '../Personalization/Favorite/FavoriteButtonsContext';
import BrochurePage from '../BrochurePage/BrochurePage';
import BrochureCommunityPrompt from './BrochureCommunityPrompt';
import { PRIVACY_POLICY_URL, PRIVACY_POLICY_TEXT } from '../../utils/privacy';

const auth = getAuthClient();

// Footer copy specific to the TourGuide AI brochure.
const TGAI_FOOTER_MESSAGE = (
  <>
    Your TourGuide AI Brochure is brought to you by
    {' '}
    <span>YOUR</span>
    TOUR, a sales enablement tool by THE VECTRE.
  </>
);

const TGAI_FOOTER_LINKS = (
  <a href={PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer">
    {PRIVACY_POLICY_TEXT}
  </a>
);

// The TourGuide AI brochure is served by two endpoints so the per-community
// half can be cached and shared across every lead:
//   - tgai_brochure_lead/{contactHash}: the lead's name + favorited amenities
//   - tgai_brochure_community/{urlSlug}: brochure content + on-brochure floor
//     plans/media (marked "Display on TourGuide AI Brochure")
// The route is /:communitySlug/:drupalNid, so both keys are in params
// and the two calls run in parallel. The lead call passes the community slug so
// Drupal scopes the lead to this community: the same contact in another
// community's TourGuide AI is a distinct lead whose favorites never bleed in.
// The result is merged into the PIB-shaped payload the mappers below expect.
export const fetchTgaiBrochureData = async ({ drupalNid, communitySlug }) => {
  const leadUrl = buildYourTourApiUrl(
    communitySlug
      ? `/your_tour_api/tgai_brochure_lead/${drupalNid}?community=${encodeURIComponent(communitySlug)}`
      : `/your_tour_api/tgai_brochure_lead/${drupalNid}`,
  );
  const communityUrl = communitySlug
    ? buildYourTourApiUrl(`/your_tour_api/tgai_brochure_community/${communitySlug}`)
    : null;

  const [lead, community] = await Promise.all([auth.fetchWithAuthentication(leadUrl, 'GET', null),
    communityUrl ? auth.fetchWithAuthentication(communityUrl, 'GET', null) : Promise.resolve(null)]);

  if (!lead || lead.error) {
    return lead || { error: 'Failed to load TourGuide AI brochure lead data' };
  }

  // The lead has been converted to a resident, so this brochure now lives at
  // the resident's own URL. Hand the redirect straight to BrochurePage rather
  // than merging a payload we are about to navigate away from.
  if (lead.redirect?.url) {
    return { redirect: lead.redirect };
  }

  const communityData = community && !community.error ? community : {};
  const communityFavorites = communityData.favorites || {};

  return {
    resident: lead.resident,
    community: communityData.community || null,
    salesCounselor: lead.salesCounselor || null,
    // Lead-scoped first-party engagement creds (endpoint + HMAC token + lead
    // identity). BrochurePage reads this generic block to fire the shared
    // beacons; null when the lead's community couldn't be resolved.
    tracking: lead.tracking || null,
    favorites: {
      residences: communityFavorites.residences || [],
      floorPlans: communityFavorites.floorPlans || [],
      amenities: lead.favorites?.amenities || [],
      media: communityFavorites.media || [],
    },
  };
};

// Map the merged response into the shape BrochurePageHeader expects.
const getTgaiHeaderData = (data) => {
  const { resident, community, salesCounselor } = data || {};

  return {
    community,
    names: resident?.names,
    createdDate: resident?.createdDate,
    // No changedDate: the TGAI brochure shows no "Last edited" line.
    // Leads have no resident message; use the community's brochure message.
    message: community?.message,
    salesCounselor,
    uuid: resident?.uuid,
    favoriteResidences: resident?.favoriteResidenceIds || [],
    favoriteAmenities: resident?.favoriteAmenityIds || [],
    favoriteFloorPlans: resident?.favoriteFloorPlanIds || [],
    favoriteMedia: resident?.favoriteMediaIds || [],
    mapResidencesFavorites: resident?.mapResidencesFavorites,
    mapFavorites: resident?.mapFavorites,
  };
};

// Map the merged response into BrochurePageContent props.
export const getTgaiContentProps = (data, params) => {
  const { community, favorites } = data || {};

  return {
    communitySlug: params.communitySlug,
    resourcesData: community?.resources,
    amenitiesServicesHTML: community?.amenitiesServicesHTML,
    aboutTitle: community?.aboutTitle,
    floorPlansTitle: community?.floorPlansPageTitle,
    buckets: community?.buckets,
    initialFavoriteData: getTgaiHeaderData(data),
    favoritesData: favorites,
  };
};

const getTgaiDocumentTitle = (data) => {
  const name = data?.community?.name;
  return `${name ? `${name} |` : ''} My Personalized Brochure`;
};

// The lead resource refuses an unscoped link when the contact hash appears in
// more than one community, because resolving it would disclose which of our
// communities this person toured to whoever holds the URL. That refusal is a
// 404 carrying this copy, and it is the one error the client can recover from
// — everything else (an unknown hash, capture switched off) is a genuine dead
// end that naming a community would not fix. Matched on the message because
// the resource has no error code to match on.
const MISSING_COMMUNITY_ERROR = /missing its community/i;

const isMissingCommunityError = (data) => (
  typeof data?.error === 'string' && MISSING_COMMUNITY_ERROR.test(data.error)
);

const WRONG_COMMUNITY_MESSAGE = 'We could not find your brochure for that community. '
  + 'Check the name and try again.';

const TourGuideAiBrochure = () => {
  // Set favorites for map context
  const { setFavorites, setResidenceFavorites } = useFavoriteButtons();

  const { communitySlug, drupalNid } = useParams();
  const history = useHistory();
  const location = useLocation();

  // Set when we sent the visitor here ourselves, from the prompt below. It is
  // the difference between "this link never had a community" and "the
  // community you named was not the right one", which need different copy.
  const cameFromPrompt = !!location.state?.fromCommunityPrompt;

  const [isAsking, setIsAsking] = useState(false);
  const [promptError, setPromptError] = useState(null);

  const handleDataLoaded = (data) => {
    if (data?.resident) {
      setFavorites(data.resident.mapFavorites);
      setResidenceFavorites(data.resident.mapResidencesFavorites);
    }
  };

  const fetchData = useCallback(async (params) => {
    const data = await fetchTgaiBrochureData(params);

    if (data?.error) {
      if (!params.communitySlug && isMissingCommunityError(data)) {
        setPromptError(null);
        setIsAsking(true);
      } else if (params.communitySlug && cameFromPrompt) {
        // The community they named did not have this brochure. Ask again
        // rather than dead-ending them on a generic error.
        setPromptError(WRONG_COMMUNITY_MESSAGE);
        setIsAsking(true);
      }
    }

    return data;
  }, [cameFromPrompt]);

  // Answering moves the visitor onto the scoped URL rather than re-fetching in
  // place: /<slug>/<token> is the URL the rest of the app is built
  // around — it is what puts the community slug in getUrlData, loads the
  // community config, and pairs the lead half with the community half.
  const handleCommunitySubmit = (slug) => {
    setIsAsking(false);
    setPromptError(null);
    history.replace(`/${encodeURIComponent(slug)}/${drupalNid}`, {
      fromCommunityPrompt: true,
    });
  };

  if (isAsking) {
    return <BrochureCommunityPrompt error={promptError} onSubmit={handleCommunitySubmit} />;
  }

  return (
    <BrochurePage
      key={communitySlug || ''}
      fetchData={fetchData}
      getDocumentTitle={getTgaiDocumentTitle}
      getHeaderData={getTgaiHeaderData}
      getContentProps={getTgaiContentProps}
      onDataLoaded={handleDataLoaded}
      footerMessage={TGAI_FOOTER_MESSAGE}
      footerLinks={TGAI_FOOTER_LINKS}
    />
  );
};

export default TourGuideAiBrochure;
